// src/components/admin/sections/AdminNews.tsx
import React, { useState, useEffect } from 'react';
import apiElCristo from '../../../service/api_elcristo';

interface Author {
  name: string;
  image: string;
  role?: string;
}

interface NewsItem {
  image: string;
  category: string;
  date: string;
  title: string;
  description?: string;
  link: string;
  author: Author;
}

interface NewsData {
  subtitle: string;
  title: string;
  featuredNews: NewsItem;
  additionalNews: NewsItem[];
}

const AdminNews: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null);
  const [isFeatured, setIsFeatured] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [newsData, setNewsData] = useState<NewsData>({
    subtitle: '',
    title: '',
    featuredNews: {
      image: '',
      category: '',
      date: '',
      title: '',
      description: '',
      link: '',
      author: { name: '', image: '', role: '' }
    },
    additionalNews: []
  });

  const [formData, setFormData] = useState<NewsItem>({
    image: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    title: '',
    description: '',
    link: '',
    author: {
      name: 'Colegio El Cristo',
      image: '/assets/kidsa/assets/img/news/author.png',
      role: 'Administración'
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getNewsData();
      setNewsData(data);
    } catch (error) {
      console.error('Error loading news:', error);
      setMessage({ type: 'error', text: 'Error al cargar las noticias' });
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewsData(prev => ({ ...prev, [name]: value }));
  };

  const handleOpenModal = (news?: NewsItem, featured: boolean = false) => {
    if (news) {
      setEditingNews(news);
      setFormData(news);
      setIsFeatured(featured);
    } else {
      setEditingNews(null);
      setFormData({
        image: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        title: '',
        description: '',
        link: '',
        author: {
          name: 'Colegio El Cristo',
          image: '/assets/kidsa/assets/img/news/author.png',
          role: 'Administración'
        }
      });
      setIsFeatured(false);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingNews(null);
    setIsFeatured(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('author.')) {
      const authorField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        author: { ...prev.author, [authorField]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveNews = () => {
    if (isFeatured) {
      setNewsData(prev => ({ ...prev, featuredNews: formData }));
    } else {
      if (editingNews) {
        // Actualizar noticia existente
        const index = newsData.additionalNews.findIndex(n => n === editingNews);
        const updated = [...newsData.additionalNews];
        updated[index] = formData;
        setNewsData(prev => ({ ...prev, additionalNews: updated }));
      } else {
        // Agregar nueva noticia
        setNewsData(prev => ({
          ...prev,
          additionalNews: [...prev.additionalNews, formData]
        }));
      }
    }
    handleCloseModal();
  };

  const handleDeleteNews = (news: NewsItem) => {
    if (!window.confirm('¿Estás seguro de eliminar esta noticia?')) {
      return;
    }

    setNewsData(prev => ({
      ...prev,
      additionalNews: prev.additionalNews.filter(n => n !== news)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      await apiElCristo.updateNewsData(newsData);
      setMessage({ type: 'success', text: '✅ Noticias actualizadas correctamente' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving news:', error);
      setMessage({ type: 'error', text: '❌ Error al guardar: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="elcristo-admin-content">
        <div className="elcristo-loading-spinner">
          <div className="elcristo-spinner"></div>
          <p>Cargando noticias...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="elcristo-admin-content">
      <div className="elcristo-content-header">
        <div>
          <h1><i className="fas fa-newspaper"></i> Gestión de Noticias</h1>
          <p>Administrar noticias y comunicados del colegio</p>
        </div>
        <button className="elcristo-btn-primary" onClick={() => handleOpenModal()}>
          <i className="fas fa-plus"></i>
          Nueva Noticia
        </button>
      </div>

      {message && (
        <div className={`elcristo-alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Section Settings */}
        <div className="elcristo-admin-form">
          <div className="elcristo-form-section">
            <h3><i className="fas fa-cog"></i> Configuración de Sección</h3>
            
            <div className="elcristo-form-row">
              <div className="elcristo-form-group">
                <label htmlFor="subtitle">Subtítulo de Sección</label>
                <input
                  type="text"
                  id="subtitle"
                  name="subtitle"
                  value={newsData.subtitle}
                  onChange={handleSectionChange}
                  placeholder="Ej: Noticias Escolares"
                />
              </div>

              <div className="elcristo-form-group">
                <label htmlFor="title">Título de Sección</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={newsData.title}
                  onChange={handleSectionChange}
                  placeholder="Ej: Explora nuestras noticias"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Featured News */}
        <div className="elcristo-news-section">
          <div className="elcristo-section-header">
            <h3><i className="fas fa-star"></i> Noticia Destacada</h3>
            <button
              type="button"
              className="elcristo-btn-secondary"
              onClick={() => handleOpenModal(newsData.featuredNews, true)}
            >
              <i className="fas fa-edit"></i>
              Editar
            </button>
          </div>

          <div className="elcristo-news-card featured">
            <div className="elcristo-news-image">
              <img src={newsData.featuredNews.image} alt={newsData.featuredNews.title} />
              <span className="elcristo-news-category">{newsData.featuredNews.category}</span>
            </div>
            <div className="elcristo-news-content">
              <div className="elcristo-news-meta">
                <i className="fas fa-calendar"></i>
                {newsData.featuredNews.date}
              </div>
              <h4>{newsData.featuredNews.title}</h4>
              <p>{newsData.featuredNews.description}</p>
              <div className="news-author">
                <img src={newsData.featuredNews.author.image} alt={newsData.featuredNews.author.name} />
                <div>
                  <strong>{newsData.featuredNews.author.name}</strong>
                  <span>{newsData.featuredNews.author.role}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional News */}
        <div className="elcristo-news-section">
          <div className="elcristo-section-header">
            <h3><i className="fas fa-list"></i> Noticias Adicionales ({newsData.additionalNews.length})</h3>
          </div>

          <div className="elcristo-news-grid">
            {newsData.additionalNews.map((news, index) => (
              <div key={index} className="elcristo-news-card">
                <div className="elcristo-news-image">
                  <img src={news.image} alt={news.title} />
                  <span className="elcristo-news-category">{news.category}</span>
                </div>
                <div className="elcristo-news-content">
                  <div className="elcristo-news-meta">
                    <i className="fas fa-calendar"></i>
                    {news.date}
                  </div>
                  <h4>{news.title}</h4>
                  <p>{news.description}</p>
                </div>
                <div className="elcristo-news-actions">
                  <button
                    type="button"
                    className="elcristo-btn-icon btn-edit"
                    onClick={() => handleOpenModal(news, false)}
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    type="button"
                    className="elcristo-btn-icon btn-delete"
                    onClick={() => handleDeleteNews(news)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}

            {newsData.additionalNews.length === 0 && (
              <div className="elcristo-empty-state">
                <i className="fas fa-newspaper"></i>
                <p>No hay noticias adicionales</p>
                <button
                  type="button"
                  className="elcristo-btn-primary"
                  onClick={() => handleOpenModal()}
                >
                  <i className="fas fa-plus"></i>
                  Agregar Primera Noticia
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="elcristo-form-actions">
          <button type="button" className="elcristo-btn-secondary" onClick={loadData} disabled={saving}>
            <i className="fas fa-undo"></i>
            Recargar
          </button>
          
          <button type="submit" className="elcristo-btn-primary" disabled={saving}>
            {saving ? (
              <>
                <span className="elcristo-spinner-small"></span>
                Guardando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Guardar Cambios
              </>
            )}
          </button>
        </div>
      </form>

      {/* Modal */}
      {showModal && (
        <div className="elcristo-modal-overlay" onClick={handleCloseModal}>
          <div className="elcristo-modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="elcristo-modal-header">
              <h2>
                <i className="fas fa-newspaper"></i>
                {isFeatured ? 'Editar Noticia Destacada' : editingNews ? 'Editar Noticia' : 'Nueva Noticia'}
              </h2>
              <button className="elcristo-modal-close" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="elcristo-modal-body">
              <div className="elcristo-form-row">
                <div className="elcristo-form-group">
                  <label>Título *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Título de la noticia"
                    required
                  />
                </div>

                <div className="elcristo-form-group">
                  <label>Categoría *</label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    placeholder="Ej: Tecnología, Deportes, Cultura"
                    required
                  />
                </div>
              </div>

              <div className="elcristo-form-group">
                <label>Descripción</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Descripción breve de la noticia"
                  rows={3}
                />
              </div>

              <div className="elcristo-form-row">
                <div className="elcristo-form-group">
                  <label>Fecha *</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="elcristo-form-group">
                  <label>Link</label>
                  <input
                    type="text"
                    name="link"
                    value={formData.link}
                    onChange={handleChange}
                    placeholder="#noticias o URL completa"
                  />
                </div>
              </div>

              <div className="elcristo-form-group">
                <label>Imagen URL *</label>
                <input
                  type="text"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  placeholder="/assets/kidsa/assets/img/news/01.jpg"
                  required
                />
                {formData.image && (
                  <div className="elcristo-image-preview">
                    <img src={formData.image} alt="Preview" />
                  </div>
                )}
              </div>

              <div className="elcristo-form-section">
                <h4><i className="fas fa-user"></i> Información del Autor</h4>
                
                <div className="elcristo-form-row">
                  <div className="elcristo-form-group">
                    <label>Nombre</label>
                    <input
                      type="text"
                      name="author.name"
                      value={formData.author.name}
                      onChange={handleChange}
                      placeholder="Nombre del autor"
                    />
                  </div>

                  <div className="elcristo-form-group">
                    <label>Rol</label>
                    <input
                      type="text"
                      name="author.role"
                      value={formData.author.role}
                      onChange={handleChange}
                      placeholder="Ej: Directora, Profesor"
                    />
                  </div>
                </div>

                <div className="elcristo-form-group">
                  <label>Imagen del Autor</label>
                  <input
                    type="text"
                    name="author.image"
                    value={formData.author.image}
                    onChange={handleChange}
                    placeholder="/assets/kidsa/assets/img/news/author.png"
                  />
                </div>
              </div>
            </div>

            <div className="elcristo-modal-footer">
              <button type="button" className="elcristo-btn-secondary" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
                Cancelar
              </button>
              <button type="button" className="elcristo-btn-primary" onClick={handleSaveNews}>
                <i className="fas fa-save"></i>
                {editingNews ? 'Actualizar' : 'Agregar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNews;